import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { Layout, Menu, Button, Space, theme } from 'antd';
import { useScrollToTop } from '../hooks/useScrollToTop';
import type { MenuProps } from 'antd';
import {
  SettingOutlined,
  UserOutlined,
  BankOutlined,
  CreditCardOutlined,
  DatabaseOutlined,
  CloudUploadOutlined,
  CloudDownloadOutlined,
  LeftCircleOutlined,
  RightCircleOutlined,
  MenuOutlined,
  ArrowLeftOutlined,
  DashboardOutlined,
  TeamOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  ToolOutlined,
  FormOutlined,
} from '@ant-design/icons';
import { HelpModal } from '../components/help/HelpModal';
import { useHelpStore } from '../stores/helpStore';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { useResponsive, LAYOUT_CONSTANTS } from '../hooks/useResponsive';
import { MobileDrawerMenu } from '../components/layout/MobileDrawerMenu';
import { FONT_FAMILY_BASE } from '../constants/fonts';
import { AppBreadcrumb, BreadcrumbProvider } from '../components/Breadcrumb';
import { trpc } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { getEnvStyles } from '../constants/environment';
import { usePermission } from '../hooks/usePermission';
import { DevRouteInfo } from '../components/common/DevRouteInfo';
import { PageMemoDrawer, PAGE_MEMO_DRAWER_WIDTH } from '../components/memo/PageMemoDrawer';
import { resolvePageMemoInfo } from '../constants/pageMemoPages';

const { Header, Sider, Content } = Layout;
const { useToken } = theme;
const MEMO_DRAWER_WIDTH_STORAGE_KEY = 'pageMemoDrawerWidth';

export function SettingsLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useToken();
  const { effectiveTheme } = useUserPreferences();
  const [collapsed, setCollapsed] = useState(false);
  const { isAuthenticated } = useAuthStore();

  // レスポンシブ対応
  const { isMobile, useMobileMenu } = useResponsive();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [memoDrawerOpen, setMemoDrawerOpen] = useState(false);
  const [memoDrawerWidth, setMemoDrawerWidth] = useState(() => {
    if (typeof window === 'undefined') return PAGE_MEMO_DRAWER_WIDTH;
    const stored = Number(window.localStorage.getItem(MEMO_DRAWER_WIDTH_STORAGE_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : PAGE_MEMO_DRAWER_WIDTH;
  });
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  // ページ遷移時にスクロール位置をトップにリセット
  const contentRef = useScrollToTop<HTMLDivElement>();

  // サブスクリプション状態を取得
  const { data: company } = trpc.company.getCurrentCompany.useQuery(
    undefined,
    {
      enabled: isAuthenticated,
      staleTime: 30000,
    }
  );

  // 退会資格の確認
  const { data: withdrawEligibility } = trpc.company.canWithdraw.useQuery(
    undefined,
    {
      enabled: isAuthenticated,
      staleTime: 30000,
    }
  );

  // 解約済みかどうか
  const isSubscriptionCanceled = company?.subscriptionStatus === 'canceled';

  // 退会メニューを表示するかどうか
  const showWithdrawMenu = withdrawEligibility?.canWithdraw === true;

  // 権限チェック
  const { isManagerOrAdmin, isAdmin } = usePermission();

  // ヘルプストア
  const { openModal: openHelpModal } = useHelpStore();

  // 現在のパスに応じたデフォルトヘルプトピックを決定
  const getDefaultHelpTopicId = useCallback((): string | undefined => {
    const path = location.pathname;

    // ユーザー管理
    if (path.startsWith('/app/settings/users')) return 'user-management-overview';

    // 会社設定
    if (path.startsWith('/app/settings/company')) return 'settings-company';
    if (path.startsWith('/app/settings/branches')) return 'settings-branches';
    if (path.startsWith('/app/settings/advanced')) return 'settings-advanced';
    if (path.startsWith('/app/settings/system-config')) return 'settings-system-config';

    // サブスクリプション
    if (path.startsWith('/app/settings/subscription')) return 'subscription-overview';

    // 退会
    if (path.startsWith('/app/settings/withdraw')) return 'subscription-cancel';

    // デフォルト
    return undefined;
  }, [location.pathname]);

  const pageMemoInfo = useMemo(
    () => resolvePageMemoInfo(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(
      MEMO_DRAWER_WIDTH_STORAGE_KEY,
      String(memoDrawerWidth)
    );
  }, [memoDrawerWidth]);

  const isDarkTheme = effectiveTheme === 'dark';
  const isMildTheme = effectiveTheme === 'mild';
  const contentPadding = isMobile ? LAYOUT_CONSTANTS.CONTENT_PADDING_MOBILE : 24;
  const headerHeight = isMobile ? LAYOUT_CONSTANTS.HEADER_HEIGHT_MOBILE : LAYOUT_CONSTANTS.HEADER_HEIGHT;
  const memoDrawerOffset = memoDrawerOpen && !isMobile ? memoDrawerWidth : 0;
  const contentPaddingRight = contentPadding + memoDrawerOffset;
  const headerBackground = isDarkTheme ? '#141414' : isMildTheme ? '#fff3d6' : '#fff';
  const headerTextColor = isDarkTheme ? '#f0f0f0' : isMildTheme ? '#5a4633' : '#000';
  const headerBoxShadow = isDarkTheme ? '0 1px 4px rgba(0,0,0,0.45)' : isMildTheme ? '0 2px 6px rgba(208, 173, 118, 0.35)' : '0 1px 4px rgba(0,21,41,.08)';
  const layoutBackground = isDarkTheme ? '#0f0f0f' : isMildTheme ? '#f8f4e9' : '#EEEEEE';
  const contentCardBackground = isDarkTheme ? '#1f1f1f' : isMildTheme ? '#fffaf0' : '#fff';
  const contentBoxShadow = isDarkTheme ? '0 1px 6px rgba(0,0,0,0.35)' : isMildTheme ? '0 2px 8px rgba(208, 173, 118, 0.26)' : '0 1px 4px rgba(0,21,41,.08)';

  const enableSubscription = import.meta.env.VITE_ENABLE_SUBSCRIPTION_FEATURES !== 'false';

  // 設定メニュー項目（解約済みの場合は契約プランのみ表示）
  const menuItems: MenuProps['items'] = isSubscriptionCanceled
    ? [
      // 解約済み: 契約プランのみ表示
      {
        key: '/app/settings/subscription',
        icon: <CreditCardOutlined />,
        label: '契約プラン',
      },
    ]
    : [
      // 通常: すべてのメニューを表示
      // ダッシュボード
      {
        key: '/app/settings',
        icon: <DashboardOutlined />,
        label: 'ダッシュボード',
      },
      // 会社設定（admin/manager のみ）
      ...(isManagerOrAdmin ? [{
        type: 'group' as const,
        label: '会社設定',
        children: [
          {
            key: '/app/settings/company',
            icon: <BankOutlined />,
            label: '会社情報',
          },
          {
            key: '/app/settings/branches',
            icon: <TeamOutlined />,
            label: 'マップ',
          },
          {
            key: '/app/settings/advanced',
            icon: <ToolOutlined />,
            label: '詳細設定',
          },
          {
            key: '/app/settings/system-config',
            icon: <SettingOutlined />,
            label: 'システム設定',
          },
        ],
      }] : []),
      // 契約（admin/manager のみ）
      ...(enableSubscription && isManagerOrAdmin ? [{
        type: 'group' as const,
        label: '契約',
        children: [
          {
            key: '/app/settings/subscription',
            icon: <CreditCardOutlined />,
            label: '契約プラン',
          },
        ],
      }] : []),
      // データ管理（admin/manager のみ）
      ...(isManagerOrAdmin ? [{
        type: 'group' as const,
        label: 'データ管理',
        children: [
          {
            key: '/app/settings/users',
            icon: <UserOutlined />,
            label: 'ユーザー管理',
          },
          {
            key: '/app/settings/storage',
            icon: <DatabaseOutlined />,
            label: 'ストレージ管理',
          },
        ],
      }] : []),
      // データユーティリティ（admin は export まで、manager は import のみ）
      ...(isManagerOrAdmin ? (() => {
        const dataUtilityItems: NonNullable<MenuProps['items']> = [];
        if (isManagerOrAdmin) {
          dataUtilityItems.push({
            key: '/app/settings/import',
            icon: <CloudUploadOutlined />,
            label: 'データインポート',
          });
        }
        if (isAdmin) {
          dataUtilityItems.push({
            key: '/app/settings/export',
            icon: <CloudDownloadOutlined />,
            label: 'データエクスポート',
          });
        }
        if (dataUtilityItems.length === 0) {
          return [];
        }
        return [{
          type: 'group' as const,
          label: 'データユーティリティ',
          children: dataUtilityItems,
        }];
      })() : []),
      // 退会（Freeプランかつ未課金の管理者のみ）
      ...(showWithdrawMenu ? [{
        type: 'group' as const,
        label: 'アカウント',
        children: [
          {
            key: '/app/settings/withdraw',
            icon: <LogoutOutlined style={{ color: '#ff4d4f' }} />,
            label: <span style={{ color: '#ff4d4f' }}>退会</span>,
          },
        ],
      }] : []),
    ];

  // メニュークリック時のハンドラー
  const handleMenuClick = (key: string) => {
    navigate(key);
  };

  // サイドバーの幅計算（モバイル時は0）
  const sidebarWidth = useMobileMenu ? 0 : (collapsed ? LAYOUT_CONSTANTS.SIDEBAR_COLLAPSED_WIDTH : LAYOUT_CONSTANTS.SIDEBAR_WIDTH);

  // 環境スタイル（本番: 緑グラデーション）
  const envStyle = getEnvStyles(
    'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
    'transparent'
  );

  // 現在のパスに最も近いメニュー項目を選択
  const getSelectedKey = () => {
    const path = location.pathname;

    // 解約済みの場合は契約プランのみ
    if (isSubscriptionCanceled) {
      return '/app/settings/subscription';
    }

    // 完全一致でダッシュボード
    if (path === '/app/settings' || path === '/app/settings/dashboard') {
      return '/app/settings';
    }
    // サブパスをチェック
    const menuPaths = [
      '/app/settings/company',
      '/app/settings/branches',
      '/app/settings/advanced',
      '/app/settings/system-config',
      '/app/settings/storage',
      '/app/settings/users',
      '/app/settings/subscription',
      '/app/settings/import',
      '/app/settings/export',
      '/app/settings/withdraw',
    ];
    for (const menuPath of menuPaths) {
      if (path.startsWith(menuPath)) return menuPath;
    }
    // デフォルトはダッシュボード
    return '/app/settings';
  };

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      {/* モバイル用ドロワーメニュー */}
      {useMobileMenu && (
        <MobileDrawerMenu
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          menuItems={menuItems}
          selectedKeys={[getSelectedKey()]}
          openKeys={openKeys}
          onOpenChange={(keys) => setOpenKeys(keys)}
          onMenuClick={handleMenuClick}
          title="設定"
          subtitle={envStyle.isProduction ? 'SETTINGS' : `SETTINGS | ${envStyle.label}`}
          headerBackground={envStyle.background}
        />
      )}

      {/* デスクトップ用サイドバー */}
      {!useMobileMenu && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          theme="light"
          breakpoint="lg"
          style={{
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* タイトル部分（固定） */}
            <div style={{
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
              background: envStyle.background,
              padding: collapsed ? '12px 8px' : '12px 16px',
              flexShrink: 0,
            }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 700,
                  lineHeight: 1.05,
                  textAlign: 'center',
                  gap: collapsed ? 2 : 4,
                  fontFamily: FONT_FAMILY_BASE,
                  transition: 'all 0.2s ease',
                }}
              >
                <span
                  style={{
                    fontSize: collapsed ? 16 : 18,
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {collapsed ? '設定' : '会社設定'}
                </span>
                <span
                  style={{
                    fontSize: collapsed ? 7 : 9,
                    letterSpacing: '-0.0em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {collapsed ? 'SET' : 'SETTINGS'}
                </span>
                {/* 環境情報表示（本番以外） */}
                {!envStyle.isProduction && (
                  <span
                    style={{
                      fontSize: collapsed ? 6 : 8,
                      opacity: 0.8,
                      marginTop: collapsed ? 0 : 2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {envStyle.label}
                  </span>
                )}
              </div>
            </div>

            {/* メニュー部分（スクロール可能） */}
            <div
              className="settings-sidebar-menu-scroll"
              style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                background: envStyle.menuTint,
              }}
            >
              <Menu
                theme="light"
                mode="inline"
                selectedKeys={[getSelectedKey()]}
                openKeys={openKeys}
                onOpenChange={(keys) => setOpenKeys(keys as string[])}
                items={menuItems}
                onClick={({ key }) => navigate(key)}
                style={{
                  borderRight: 0,
                  paddingTop: 8,
                  background: 'transparent',
                }}
              />
            </div>
          </div>
          <style
            dangerouslySetInnerHTML={{
              __html: `
              .ant-menu-submenu > .ant-menu-submenu-title {
                font-weight: 500 !important;
              }
              .ant-menu-item {
                margin-bottom: 1px !important;
              }
              .ant-menu-submenu {
                margin-bottom: 1px !important;
              }
              /* グループヘッダーのスタイル */
              .ant-menu-item-group-title {
                font-size: 11px !important;
                color: ${token.colorTextSecondary} !important;
                padding: 8px 16px 2px !important;
                text-transform: uppercase !important;
                letter-spacing: 0.5px !important;
                border-top: 1px solid ${token.colorBorderSecondary} !important;
                margin-top: 4px !important;
              }
              /* 最初のグループは上線なし */
              .ant-menu-item-group:first-child .ant-menu-item-group-title {
                border-top: none !important;
                margin-top: 0 !important;
              }
              /* スクロールバーを非表示 */
              .settings-sidebar-menu-scroll {
                -ms-overflow-style: none !important;
                scrollbar-width: none !important;
              }
              .settings-sidebar-menu-scroll::-webkit-scrollbar {
                display: none !important;
                width: 0 !important;
                height: 0 !important;
              }
              /* Sider内部のレイアウト調整 */
              .ant-layout-sider-children {
                display: flex !important;
                flex-direction: column !important;
                height: 100% !important;
              }
            `
            }}
          />
        </Sider>
      )}

      <Layout style={{
        marginLeft: sidebarWidth,
        transition: 'margin-left 0.2s',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: layoutBackground,
        color: headerTextColor,
      }}>
        <Header style={{
          padding: `0 ${contentPadding}px`,
          background: headerBackground,
          color: headerTextColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: headerBoxShadow,
          position: 'fixed',
          top: 0,
          right: memoDrawerOffset,
          left: sidebarWidth,
          height: headerHeight,
          lineHeight: `${headerHeight}px`,
          zIndex: 99,
          transition: 'left 0.2s, right 0.2s, background 0.2s',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* モバイル: ハンバーガーメニュー */}
            {useMobileMenu ? (
              <MenuOutlined
                style={{ fontSize: 20, cursor: 'pointer', padding: 8 }}
                onClick={() => setMobileDrawerOpen(true)}
              />
            ) : (
              /* デスクトップ: サイドバー折り畳みトグル */
              collapsed ? (
                <RightCircleOutlined
                  style={{ fontSize: 18, cursor: 'pointer' }}
                  onClick={() => setCollapsed(false)}
                />
              ) : (
                <LeftCircleOutlined
                  style={{ fontSize: 18, cursor: 'pointer' }}
                  onClick={() => setCollapsed(true)}
                />
              )
            )}
          </div>

          <Space size={isMobile ? 8 : 16} align="center">
            <Button
              type="text"
              icon={<FormOutlined />}
              onClick={() => setMemoDrawerOpen((prev) => !prev)}
              aria-label="メモ"
            />
            {/* ヘルプアイコン */}
            <Button
              type="text"
              icon={<QuestionCircleOutlined />}
              onClick={() => openHelpModal(getDefaultHelpTopicId())}
              aria-label="ヘルプ"
            />
            {/* アプリに戻るボタン */}
            <Link to="/app/dashboard">
              <Button
                type="primary"
                icon={<ArrowLeftOutlined />}
                style={{
                  background: isDarkTheme ? '#177ddc' : isMildTheme ? '#d48806' : undefined,
                  borderColor: isDarkTheme ? '#177ddc' : isMildTheme ? '#d48806' : undefined,
                }}
              >
                {!isMobile && 'アプリへ戻る'}
              </Button>
            </Link>
          </Space>
        </Header>

        <Content
          ref={contentRef}
          style={{
            marginTop: headerHeight,
            padding: contentPadding,
            paddingRight: contentPaddingRight,
            overflowY: 'auto',
            height: `calc(100vh - ${headerHeight}px)`,
            background: layoutBackground,
            transition: 'background 0.2s, padding-right 0.2s',
          }}
        >
          <div style={{
            background: contentCardBackground,
            borderRadius: isMobile ? 4 : 8,
            padding: contentPadding,
            minHeight: `calc(100vh - ${headerHeight + contentPadding * 2}px)`,
            boxShadow: contentBoxShadow,
            color: headerTextColor,
            transition: 'background 0.2s',
          }}>
            <BreadcrumbProvider>
              <AppBreadcrumb layoutType="settings" />
              <Outlet />
            </BreadcrumbProvider>
          </div>
        </Content>

        {/* 開発環境でのみ表示されるファイルパス情報 */}
        <DevRouteInfo />

        {/* 一元メモドロワー */}
        <PageMemoDrawer
          open={memoDrawerOpen}
          onClose={() => setMemoDrawerOpen(false)}
          pageKey={pageMemoInfo?.pageKey ?? null}
          pageLabel={pageMemoInfo?.label}
          width={memoDrawerWidth}
          onWidthChange={setMemoDrawerWidth}
          resizable={!isMobile}
        />

        {/* ヘルプモーダル */}
        <HelpModal />
      </Layout>
    </Layout>
  );
}
